import {
  AllStorylineFeedThreadSortOrder,
  AttachableLinkFeedThreadSortOrder,
  BookmarkFeedThreadSortOrder,
  BroadcastFeedThreadSortOrder,
  BroadcastFeedType,
  CampaignFeedThreadSortOrder,
  CampaignFeedType,
  FollowedStorylineFeedThreadSortOrder,
  GroupFeedThreadSortOrder,
  GroupFeedType,
  HomeFeedCardThreadSortOrder,
  HomeFeedType,
  HomeMomentsFeedThreadSortOrder,
  HomeMomentsFeedType,
  InboxFeedDraftThreadSortOrder,
  InboxFeedType,
  InboxThreadSortOrder,
  MyLeadersFeedType,
  NetworkQuestionFeedThreadSortOrder,
  NetworkQuestionFeedType,
  NetworkQuestionInboxFeedType,
  TeamsMeetingFeedThreadSortOrder,
  TeamsMeetingFeedType,
  TopicFeedThreadSortOrder,
  TopicFeedType,
  TopicNetworkQuestionFeedThreadSortOrder,
  TopicNetworkQuestionFeedType,
  UserFeedThreadSortOrder,
  UserMomentsFeedThreadSortOrder,
  UserMomentsFeedType,
  UserWallFeedThreadSortOrder,
} from '../../normalization/types';
import {
  Entity,
  EntityMap,
  Group,
  LiveEvent,
  TeamsMeeting,
  TelemetryContext,
  ThreadEdge,
  Topic,
  User,
  UserSuggestionCardEdge,
} from '../types';

export enum FeedType {
  AttachableLink = 'AttachableLink',
  Campaign = 'Campaign',
  Bookmark = 'Bookmark',
  InboxDraft = 'InboxDraft',
  Group = 'Group',
  Home = 'Home',
  HomeMoments = 'HomeMoments',
  Inbox = 'Inbox',
  NetworkQuestionInbox = 'NetworkQuestionInbox',
  LeaderEmployees = 'LeaderEmployees',
  LeadersDiscovery = 'LeadersDiscovery',
  LiveEvent = 'LiveEvent',
  MyLeaders = 'MyLeaders',
  DiscoveryNetworkQuestion = 'DiscoveryNetworkQuestion',
  NetworkQuestion = 'NetworkQuestion',
  SingleNetworkQuestion = 'SingleNetworkQuestion',
  StorylineAll = 'StorylineAll',
  StorylineDiscovery = 'StorylineDiscovery',
  StorylineFollowed = 'StorylineFollowed',
  TeamsMeeting = 'TeamsMeeting',
  Topic = 'Topic',
  TopicNetworkQuestion = 'TopicNetworkQuestion',
  User = 'User',
  UserMoments = 'UserMoments',
  UserWall = 'UserWall',
}

export type PinnableFeedType = Extract<
  FeedType,
  FeedType.Campaign | FeedType.UserWall | FeedType.LiveEvent | FeedType.Group | FeedType.TeamsMeeting
>;

export type MomentsFeedType = FeedType.HomeMoments | FeedType.UserMoments;

export type StorylineHomeFeedType = Extract<
  FeedType,
  FeedType.StorylineAll | FeedType.StorylineDiscovery | FeedType.StorylineFollowed
>;

export enum HomeFeedCardEdgeType {
  Thread = 'Thread',
  HomeFeedUserSuggestionCard = 'HomeFeedUserSuggestionCard',
}

export enum FeedLocation {
  Unknown = 'Unknown',
  ViewConversationPage = 'ViewConversationPage',
  OutlookViewConversationPage = 'OutlookViewConversationPage',
}

export type FeedItemEdge = ThreadEdge | UserSuggestionCardEdge;

export type FeedThreadSortOrder =
  | AllStorylineFeedThreadSortOrder
  | AttachableLinkFeedThreadSortOrder
  | CampaignFeedThreadSortOrder
  | BookmarkFeedThreadSortOrder
  | BroadcastFeedThreadSortOrder
  | FollowedStorylineFeedThreadSortOrder
  | GroupFeedThreadSortOrder
  | HomeFeedCardThreadSortOrder
  | InboxFeedDraftThreadSortOrder
  | HomeMomentsFeedThreadSortOrder
  | InboxThreadSortOrder
  | NetworkQuestionFeedThreadSortOrder
  | TeamsMeetingFeedThreadSortOrder
  | TopicFeedThreadSortOrder
  | TopicNetworkQuestionFeedThreadSortOrder
  | UserFeedThreadSortOrder
  | UserMomentsFeedThreadSortOrder
  | UserWallFeedThreadSortOrder;

const staticSorts = ['CREATED_AT', 'CREATED_OR_PUBLISHED_AT'];
type IsStaticFeedSort = (sortOrder: FeedThreadSortOrder | undefined) => boolean;
export const isStaticFeedSort: IsStaticFeedSort = (sortOrder) => !!sortOrder && staticSorts.includes(sortOrder);

export interface Feed extends Entity {
  readonly type: FeedType;
  readonly subtype?:
    | BroadcastFeedType
    | CampaignFeedType
    | GroupFeedType
    | HomeFeedType
    | HomeMomentsFeedType
    | InboxFeedType
    | MyLeadersFeedType
    | NetworkQuestionInboxFeedType
    | NetworkQuestionFeedType
    | TeamsMeetingFeedType
    | TopicFeedType
    | TopicNetworkQuestionFeedType
    | UserMomentsFeedType;

  readonly entityId?: Group['id'] | User['id'] | Topic['id'] | LiveEvent['id'] | TeamsMeeting['id'];
  readonly sortedBy: FeedThreadSortOrder;
  readonly threadEdges: ThreadEdge[];
  readonly userSuggestionCardEdges?: UserSuggestionCardEdge[];
  readonly hasOlderThreads: boolean;
  readonly realtimeChannelId?: string;
  readonly telemetryContext: TelemetryContext;
  readonly totalPreviousThreadCount?: number;
  readonly totalNextThreadCount?: number;
}

export interface GroupFeedParameters {
  readonly isNew?: boolean;
  readonly postType?: GroupFeedType.ALL | GroupFeedType.UNANSWERED_QUESTIONS | GroupFeedType.QUESTIONS;
  readonly sortBy?:
    | GroupFeedThreadSortOrder.CREATED_AT
    | GroupFeedThreadSortOrder.UPDATED_AT
    | GroupFeedThreadSortOrder.RECOMMENDED;
}
export type UserFeedType = FeedType.User | FeedType.UserWall;

export type UserFeedSortType =
  | UserFeedThreadSortOrder.CREATED_AT
  | UserFeedThreadSortOrder.UPDATED_AT
  | UserWallFeedThreadSortOrder.CREATED_AT
  | UserWallFeedThreadSortOrder.UPDATED_AT;

export type StorylineFollowingFeedSortType =
  | FollowedStorylineFeedThreadSortOrder.CREATED_AT
  | FollowedStorylineFeedThreadSortOrder.UPDATED_AT;

export interface UserFeedParameters {
  readonly type: UserFeedType;
  readonly sortBy?: UserFeedSortType;
}

export interface StorylinePageQueryParameters {
  readonly sortBy: StorylineFollowingFeedSortType;
  readonly feedType: StorylineHomeFeedType;
}

export const defaultStorylineSortBy = UserWallFeedThreadSortOrder.CREATED_AT;
export const defaultUserSortBy = UserFeedThreadSortOrder.CREATED_AT;
export const defaultStorylineFollowingSortBy = FollowedStorylineFeedThreadSortOrder.CREATED_AT;
export const defaultStorylineFeedType = FeedType.StorylineDiscovery;
export const defaultStorylineFeedTypeKey = 'feed';

export interface TeamsMeetingFeedParameters {
  readonly filterBy?: TeamsMeetingFeedType;
  readonly sortBy?:
    | TeamsMeetingFeedThreadSortOrder.CREATED_OR_PUBLISHED_AT
    | TeamsMeetingFeedThreadSortOrder.UPVOTE_RANK_THEN_CREATED_OR_PUBLISHED_AT
    | TeamsMeetingFeedThreadSortOrder.UPDATED_AT;
}

export type Feeds = EntityMap<Feed>;
